import { Injectable, inject } from '@angular/core';
import { EMPTY, Observable, catchError, of, switchMap, throwError } from 'rxjs';
import {
  ServerError,
  historyJobCount,
  serverErrorCode,
} from '../../interfaces/server-error.interface';
import { DialogService } from '../dialog/dialog.service';
import type { WorkflowDeletionImpact } from '../workflow/workflow.service';
import { ExecutionPlanService } from './execution-plan.service';

/**
 * Whether a failed deletion-impact call means the server predates the
 * endpoint: a 501, or a 404 without the server's error envelope (a missing
 * routine is a 404 that carries one).
 */
function lacksDeletionImpact(err: { status?: unknown; error?: unknown } | null | undefined): boolean {
  return err?.status === 501 || (err?.status === 404 && serverErrorCode(err) === undefined);
}

/**
 * Why a routine delete failed, for the caller to show: the server's message
 * (a job still executing is explained only there), else the error's own.
 */
export function routineDeleteErrorMessage(err: unknown, fallback = 'Failed to delete routine'): string {
  const e = err as { error?: unknown; message?: unknown } | null | undefined;
  const envelope = (e?.error as Partial<ServerError> | undefined)?.message;
  if (typeof envelope === 'string' && envelope) return envelope;
  return typeof e?.message === 'string' && e.message ? e.message : fallback;
}

/**
 * Deletes a routine, asking first, and saying how much job history goes with
 * it (EHQ-536). Every routine delete in the app goes through here, so none
 * deletes a routine's jobs without saying so.
 */
@Injectable({ providedIn: 'root' })
export class RoutineDeleteService {
  readonly #plans = inject(ExecutionPlanService);
  readonly #dialog = inject(DialogService);

  /**
   * Count the routine's started jobs, ask once (naming that history when there
   * is any), then delete. Emits once when the routine is gone; completes
   * without emitting when the user declines; errors when the delete fails,
   * including while one of its jobs is still executing. A server without the
   * count endpoint is asked plainly, and its 409 for history asked about after.
   */
  deleteRoutine(projectId: number, routineId: number, name: string): Observable<void> {
    return this.#plans.getDeletionImpact(projectId, routineId).pipe(
      catchError((err) => (lacksDeletionImpact(err) ? of(null) : throwError(() => err))),
      switchMap((impact) => {
        if (impact && impact.executingCount > 0) {
          return throwError(
            () =>
              new Error(
                `${impact.executingCount} job(s) of routine "${name}" are still executing; wait for them or cancel them before deleting.`,
              ),
          );
        }
        return this.#confirm(name, impact).pipe(
          switchMap((confirmed) =>
            confirmed ? this.#delete(projectId, routineId, (impact?.jobCount ?? 0) > 0) : EMPTY,
          ),
        );
      }),
    );
  }

  /**
   * Delete, and if the server still refuses for history (a job started while
   * the prompt was open, or a server that couldn't count), ask with its count
   * and retry.
   */
  #delete(projectId: number, routineId: number, deleteHistory: boolean): Observable<void> {
    return this.#plans.deletePlan(projectId, routineId, deleteHistory).pipe(
      catchError((err) => {
        const jobCount = historyJobCount(err);
        if (jobCount === undefined || deleteHistory) {
          return throwError(() => err);
        }
        return this.#confirmHistory(jobCount).pipe(
          switchMap((confirmed) =>
            confirmed ? this.#plans.deletePlan(projectId, routineId, true) : EMPTY,
          ),
        );
      }),
    );
  }

  #confirm(name: string, impact: WorkflowDeletionImpact | null): Observable<boolean> {
    const jobCount = impact?.jobCount ?? 0;
    const history =
      jobCount > 0
        ? ` It has execution history from ${jobCount === 1 ? '1 job' : `${jobCount} jobs`}; deleting the routine also deletes that history and the files ${jobCount === 1 ? 'it' : 'they'} produced, and this can't be undone.`
        : '';
    return this.#dialog.confirm({
      title: 'Delete Routine',
      message: `Delete routine "${name}"?${history}`,
      confirmCaption: 'Delete',
      cancelCaption: 'Cancel',
      isDestructive: true,
    });
  }

  #confirmHistory(jobCount: number): Observable<boolean> {
    const jobs = jobCount === 1 ? '1 job' : `${jobCount} jobs`;
    const they = jobCount === 1 ? 'it' : 'they';
    return this.#dialog.confirm({
      title: 'Delete Execution History',
      message: `This routine has execution history from ${jobs}. Deleting it also deletes that history and the files ${they} produced, and this can't be undone. Do you want to continue?`,
      confirmCaption: 'Delete',
      cancelCaption: 'Cancel',
      isDestructive: true,
    });
  }
}
